'use client';

import { IMAGES_BASE_URL } from '@/lib/config';
import { capitalizeCityName } from '@/lib/utils';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { Loader2, Search } from 'lucide-react';

import { IonButton, IonChip, IonContent, IonHeader, IonItem, IonList, IonModal, IonSearchbar, IonToolbar } from '@ionic/react';
import { useTranslation } from 'react-i18next';

import { api } from '@/utils/api';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ImageWithFallback } from './ui/fallbackImage';

export type Course = {
  id: number;
  name: string;
  city: string;
  country: string;
  distance: number;
  thumbnail: string;
};

type GolfClubSearchProps = {
  onCourseSelect?: (course: Course) => void;
  mode?: 'Search' | 'Select';
  customSearchCaption?: string;
  iconOnly?: boolean;
  initialValue?: Course;
  className?: string;
};

export default function GolfClubSearch({
  onCourseSelect,
  mode = 'Select',
  customSearchCaption,
  initialValue,
  className
}: GolfClubSearchProps) {
  const { t } = useTranslation('MGP');
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [nearestCourses, setNearestCourses] = useState<Course[]>([]);
  const [value, setValue] = useState<Course | null>(initialValue || null);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTouched, setSearchTouched] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement>(null);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && location === null) getGeolocation();
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  };

  const getGeolocation = async () => {
    try {
      // Get location based on platform
      if (Capacitor.isNativePlatform()) {
        // For native platforms, use Capacitor's permission system
        const permissionStatus = await Geolocation.checkPermissions();

        // Request permissions if not granted (handles both 'denied' and 'prompt')
        if (permissionStatus.location !== 'granted') {
          const permissionRequest = await Geolocation.requestPermissions();
          if (permissionRequest.location !== 'granted') {
            throw new Error('Location permission not granted');
          }
        }

        const { coords } = await Geolocation.getCurrentPosition();
        setLocation({ latitude: coords.latitude, longitude: coords.longitude });
      } else {
        // For web, skip the Capacitor permission request and use browser API directly
        console.log('Using web geolocation API');
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            console.log('Web location found:', coords.latitude, coords.longitude);
            setLocation({ latitude: coords.latitude, longitude: coords.longitude });
          },
          (err) => {
            console.warn('Web Geolocation error:', err);
            // alert(' location: ' + err.message);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    } catch (err) {
      console.warn('Geolocation error:', err);
    }
  };

  const fetchCourses = async (query: string) => {
    try {
      let searchParams = query;
      if (location !== null) {
        const locationParams = `latitude=${encodeURIComponent(location.latitude)}&longitude=${encodeURIComponent(location.longitude)}`;
        searchParams = searchParams ? `${searchParams}&${locationParams}` : locationParams;
      }
      const { data } = await api.get(`/golf-clubs?${searchParams}`);
      if (!data) throw new Error('No data received from API');
      return data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    }
  };

  const fetchNearestCourses = useCallback(async () => {
    if (!location) return;
    const data = await fetchCourses('');
    setNearestCourses(data);
    setCourses(data);
  }, [location]);

  const handleSearch = useCallback(
    async (value: string) => {
      setIsSearching(true);
      setSearchTouched(true);

      if (value.trim()) {
        const query = `query=${encodeURIComponent(value.trim())}`;
        const data = await fetchCourses(query);
        setCourses(data);
      } else {
        setCourses(nearestCourses);
      }

      setIsSearching(false);
    },
    [nearestCourses]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(inputValue);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, handleSearch]);

  useEffect(() => {
    if (open && location) fetchNearestCourses();
  }, [open, location, fetchNearestCourses]);

  const handleSelectCourse = (course: Course) => {
    if (mode === 'Search') {
      history.push(`/c/${course.id}`);
    } else {
      setValue(course);
      onCourseSelect?.(course);
    }
    setOpen(false);
    setSearchTouched(false);
    setCourses([]);
  };

  return (
    <>
      <IonButton
        type="button"
        color="transparent"
        role="combobox"
        onClick={() => handleOpenChange(!open)}
        className={`w-full text-foreground justify-start text-start rounded-md font-normal ${mode === 'Search' ? ' text-muted-foreground' : 'bg-card'
          } ${className}`}
      >
        {mode === 'Select' && value && (
          <ImageWithFallback
            alt={`${value?.name} photo`}
            fallbackImage="/assets/images/fallback.jpg"
            src={`${IMAGES_BASE_URL}/${value?.id}.jpg`}
            className="size-10 shrink-0 overflow-hidden object-cover rounded mr-2"
          />
        )}
        {value ? (
          <span className="flex items-center font-semibold gap-3 w-full">
            {capitalizeCityName(value.name)}
          </span>
        ) : (
          mode === 'Select' ? (
            <span className="flex items-center justify-between gap-1 w-full font-bold">
              <span>{customSearchCaption || t('search_golf_course_placeholder')}</span>
              <ChevronRightIcon className="size-5" />
            </span>
          ) : (
            <span className="flex items-center gap-1 w-full">
              <Search className="size-5" />
              <span>{customSearchCaption || t('search_golf_course_placeholder')}</span>
            </span>
          )
        )}
      </IonButton >
      <IonModal isOpen={open} onDidDismiss={() => handleOpenChange(false)}>
        <IonHeader>
          <IonToolbar className='mt-2'>
            <IonSearchbar
              ref={searchRef}
              showCancelButton="always"
              onIonCancel={() => handleOpenChange(false)}
              cancelButtonText={t('cancel')}
              autoFocus
              autocomplete='off'
              name="selectedCourse"
              className="p-0"
              placeholder={t('search_golf_course_placeholder')}
              onIonInput={(e) => setInputValue(e.detail.value || '')}
            />
          </IonToolbar>
        </IonHeader>

        <IonContent>

          <IonList lines='none' className="flex-1 overflow-y-auto bg-card">
            {!isSearching && courses.length > 0 && <div className="px-5 py-2 text-sm font-medium">{inputValue ? t('golf_clubs') : t('near_golf_clubs')}</div>}
            {isSearching ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              </div>
            ) : courses.length > 0 ? (
              courses.map((course) => (
                <IonItem
                  color="transparent p-0 m-0"
                  key={course.id}
                  onClick={() => handleSelectCourse(course)}
                  style={{
                    '--padding-start': '0px',
                    '--padding-end': '0px'
                  }}
                >
                  <ImageWithFallback
                    alt={`${course.name} photo`}
                    fallbackImage="/assets/images/course_fallback.jpg"
                    src={`${IMAGES_BASE_URL}/${course.id}.jpg`}
                    className="size-16 p-2 shrink-0 overflow-hidden object-cover rounded-md"
                  />
                  <div>
                    <span className="font-semibold">{capitalizeCityName(course.name)}</span>
                    <div className="text-sm text-gray-500">{capitalizeCityName(course.city)}, {course.country}</div>
                  </div>
                  {course.distance && (
                    <IonChip slot="end" color="primary">
                      {course.distance} km
                    </IonChip>

                  )}
                </IonItem>
              ))
            ) : (
              <>
                {!isSearching && inputValue.length === 0 && location == null && (
                  <div hidden={isSearching || location != null} className="flex flex-col justify-center items-center mt-8 gap-4 py-6">
                    <div className="flex flex-col items-center justify-center">
                      {/* <span className="relative flex size-16">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50 opacity-75"></span>
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full delay-700 bg-primary/50 opacity-75"></span>
                        <span className="absolute inline-flex rounded-full bg-primary/80 size-full justify-center items-center" ><MapPinned color='white' size={24} /></span>
                      </span> */}
                      <img src="/assets/images/NoGPS.svg" alt="Location" />
                      {/* <h1 className="text-2xl mt-8 font-semibold">Location not found</h1> */}
                      <p className="max-w-sm text-center my-4 px-4 text-muted-foreground">
                        {t('enable_location_tip')}
                      </p>
                      <IonButton
                        shape='round'
                        onClick={getGeolocation}
                      >
                        {t('enable_location')}
                      </IonButton>
                    </div>
                  </div>
                )
                }

                {inputValue.length > 0 && (
                  <div className="flex text-center items-center justify-center py-6" hidden={!searchTouched || isSearching || inputValue.length === 0}>
                    {t('no_results')}
                  </div>
                )}
              </>
            )}
          </IonList>
        </IonContent>
      </IonModal >
    </>
  );
}